import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import "../PtDashboard/ptDashboard.css";

const num = (v) => Number(v || 0).toLocaleString("en-PK");

function ClientPicker({ clients, value, onPick, placeholder, inputId }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selected = clients.find((c) => String(c.id) === String(value));
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return clients
      .filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          String(c.id).includes(q) ||
          String(c.phone || "").includes(q) ||
          String(c.uid || "").toLowerCase().includes(q)
      )
      .slice(0, 25);
  }, [clients, q]);

  return (
    <div className="position-relative">
      <input
        id={inputId}
        type="text"
        className="form-control"
        autoComplete="off"
        placeholder={placeholder}
        value={open ? query : selected ? `${selected.name} (#${selected.id})` : ""}
        onFocus={() => {
          setQuery("");
          setOpen(true);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 180);
        }}
      />
      {open && q ? (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 30, maxHeight: 240, overflowY: "auto" }}
        >
          {matches.length ? (
            matches.map((c) => (
              <li
                key={c.id}
                className="list-group-item list-group-item-action py-1"
                style={{ cursor: "pointer" }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onPick(c);
                  setOpen(false);
                }}
              >
                {c.name}{" "}
                <span className="text-muted">
                  #{c.id}
                  {c.phone ? ` · ${c.phone}` : ""}
                </span>
              </li>
            ))
          ) : (
            <li className="list-group-item text-muted py-1">No client in your branch</li>
          )}
        </ul>
      ) : null}
    </div>
  );
}

const Kpi = ({ label, value, sub, tone, icon }) => (
  <div className={`pt-kpi pt-tone-${tone}`}>
    <div className="pt-kpi-body">
      <p className="pt-kpi-label">{label}</p>
      <h4 className="pt-kpi-value">{value}</h4>
      {sub ? <p className="pt-kpi-sub">{sub}</p> : null}
    </div>
    <div className="pt-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

const METRIC_CHIPS = [
  { key: "weight", label: "Weight", unit: "kg", lowerIsBetter: true },
  { key: "body_mass_index", label: "BMI", unit: "", lowerIsBetter: true },
  { key: "fat", label: "Fat", unit: "%", lowerIsBetter: true },
  { key: "waist", label: "Waist", unit: "in", lowerIsBetter: true },
  { key: "chest", label: "Chest", unit: "in", lowerIsBetter: false },
  { key: "rhr", label: "RHR", unit: "bpm", lowerIsBetter: true },
];

const deltaLabel = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!n) return null;
  return `${n > 0 ? "+" : ""}${n}`;
};

const deltaClass = (value, lowerIsBetter) => {
  const n = Number(value || 0);
  if (!n) return "";
  const good = lowerIsBetter ? n < 0 : n > 0;
  return good ? " pt-delta-good" : " pt-delta-bad";
};

const conversionPill = (value) => {
  const v = String(value || "Pending").toLowerCase();
  if (v === "yes") return "pt-pill-yes";
  if (v === "no") return "pt-pill-no";
  return "pt-pill-pending";
};

const emptyReferral = {
  client_id: "",
  client_name: "",
  trainer_id: "",
  referral_date: moment().format("YYYY-MM-DD"),
  notes: "",
};

export default function GtDashboard() {
  const { state } = useContext(NoteState);
  const { branch_id, first_name, last_name } = state;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().startOf("month").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
  const [clientFilter, setClientFilter] = useState("");
  const [assessClientId, setAssessClientId] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [referral, setReferral] = useState(emptyReferral);

  const load = useCallback(async () => {
    if (!branch_id) return;
    setLoading(true);
    setError("");
    try {
      const res = await UserServices.gtDashboardSummary({
        branch_id,
        start_date: startDate,
        end_date: endDate,
        client_id: clientFilter || undefined,
      });
      setData(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setData(null);
      setError(err?.response?.data?.message || "Could not load GT Dashboard.");
    } finally {
      setLoading(false);
    }
  }, [branch_id, startDate, endDate, clientFilter]);

  const loadClients = useCallback(async () => {
    if (!branch_id) return;
    try {
      const res = await UserServices.getClientsName(branch_id);
      const rows = (res?.data?.data || []).map((c) => ({
        id: c.id,
        name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
        phone: c.phone,
        uid: c.uid,
      }));
      setClients(rows);
    } catch (err) {
      console.error(err);
      setClients([]);
    }
  }, [branch_id]);

  useEffect(() => {
    load();
  }, [load]);

  const loadTimeline = useCallback(async () => {
    if (!assessClientId) {
      setTimeline(null);
      return;
    }
    setTimelineLoading(true);
    try {
      const res = await UserServices.gtClientAssessments({
        client_id: assessClientId,
      });
      setTimeline(res?.data?.data || null);
    } catch (err) {
      console.error(err);
      setTimeline(null);
      toast.error(
        err?.response?.data?.message || "Could not load assessment timeline."
      );
    } finally {
      setTimelineLoading(false);
    }
  }, [assessClientId]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const trainers = data?.trainers || [];
  const assessments = data?.assessments || [];
  const tlItems = (timeline?.timeline || []).filter(
    (item) => item.kind !== "questionnaire" && !/pre assessment/i.test(item.type || "")
  );
  const tlStats = timeline?.stats || {};
  const tlClient = timeline?.client || null;
  const referrals = data?.referrals || [];
  const stats = data?.referral_stats || {};

  const submitReferral = async (e) => {
    e.preventDefault();
    if (!referral.client_id || !referral.trainer_id || !referral.referral_date) {
      toast.error("Client, PT, and referral date are required.");
      return;
    }
    setSaving(true);
    try {
      await UserServices.gtStoreBefitReferral({
        branch_id,
        client_id: referral.client_id,
        trainer_id: referral.trainer_id,
        referral_date: referral.referral_date,
        notes: referral.notes || null,
      });
      toast.success("BeFit referral saved.");
      setReferral(emptyReferral);
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message?.client_id?.[0] ||
          err?.response?.data?.message ||
          "Could not save referral."
      );
    } finally {
      setSaving(false);
    }
  };

  const updateConversion = async (id, converted_to_pt) => {
    try {
      await UserServices.gtUpdateBefitConversion(id, {
        converted_to_pt,
        converted_at:
          converted_to_pt === "Pending" ? null : moment().format("YYYY-MM-DD"),
      });
      toast.success("Conversion updated.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not update conversion.");
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design pt-shell">
          <MenuButton />
          <div className="container-fluid px-3 px-md-4 pb-4">
            <div className="pt-topbar">
              <div>
                <p className="pt-eyebrow">General Trainer</p>
                <h2 className="pt-title">
                  <i className="bi bi-clipboard2-pulse me-2"></i>
                  GT Dashboard
                </h2>
                <p className="pt-subtitle">
                  {moment().format("dddd, DD MMM YYYY")}
                  {first_name
                    ? ` · Welcome ${first_name}${last_name ? ` ${last_name}` : ""}`
                    : ""}
                </p>
              </div>
              <div className="pt-toolbar">
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  aria-label="From date"
                />
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  aria-label="To date"
                />
                <select
                  className="form-select"
                  value={clientFilter}
                  onChange={(e) => setClientFilter(e.target.value)}
                  aria-label="Client"
                >
                  <option value="">All clients</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} (#{c.id})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={load}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </button>
              </div>
            </div>

            {error ? <div className="alert alert-warning py-2">{error}</div> : null}

            {loading && !data ? (
              <div className="text-center py-5">
                <div className="spinner-border text-danger" role="status" />
                <div className="text-muted mt-2 small">Loading GT dashboard…</div>
              </div>
            ) : (
              <>
                <h3 className="pt-section-title">
                  {moment(startDate).format("DD MMM")} –{" "}
                  {moment(endDate).format("DD MMM YYYY")} at a glance
                </h3>
                <div className="pt-kpi-grid">
                  <Kpi
                    label="Assessments"
                    value={num(data?.assessment_count)}
                    sub="In the selected date range"
                    icon="bi-clipboard2-check"
                    tone="teal"
                  />
                  <Kpi
                    label="BeFit Referrals"
                    value={num(stats.total)}
                    sub="Everyone referred to BeFit"
                    icon="bi-send"
                    tone="blue"
                  />
                  <Kpi
                    label="Converted to PT"
                    value={num(stats.converted)}
                    sub="Marked as signed up"
                    icon="bi-person-check"
                    tone="green"
                  />
                  <Kpi
                    label="Pending"
                    value={num(stats.pending)}
                    sub="Awaiting conversion decision"
                    icon="bi-hourglass-split"
                    tone="amber"
                  />
                  <Kpi
                    label="Not Converted"
                    value={num(stats.not_converted)}
                    sub="Did not take PT"
                    icon="bi-x-circle"
                    tone="red"
                  />
                </div>

                <h3 className="pt-section-title">Client assessments</h3>
                <div className="row g-3 mb-4">
                  <div className="col-12 col-xl-7">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>Assessment timeline</h5>
                        <span className="pt-chip">
                          {tlClient
                            ? `${num(tlStats.total)} entries for ${tlClient.name}`
                            : `${num(assessments.length)} records in range`}
                        </span>
                      </div>
                      <div className="row g-2 align-items-end mb-3">
                        <div className="col-md-7">
                          <label className="pt-field-label" htmlFor="gt-assess-client">
                            Client timeline
                          </label>
                          <ClientPicker
                            inputId="gt-assess-client"
                            clients={clients}
                            value={assessClientId}
                            placeholder="Type a name, ID, or phone from your branch"
                            onPick={(c) => setAssessClientId(c ? String(c.id) : "")}
                          />
                        </div>
                        <div className="col-md-5">
                          <div className="pt-row-actions">
                            <Link
                              className={`btn btn-sm btn-outline-danger${
                                assessClientId ? "" : " disabled"
                              }`}
                              to={
                                assessClientId
                                  ? `/postAssessment/${assessClientId}`
                                  : "#"
                              }
                              onClick={(e) => {
                                if (!assessClientId) e.preventDefault();
                              }}
                            >
                              Add Assessment
                            </Link>
                            <Link
                              className={`btn btn-sm btn-outline-dark${
                                assessClientId ? "" : " disabled"
                              }`}
                              to={
                                assessClientId
                                  ? `/viewAssessment/${assessClientId}`
                                  : "#"
                              }
                              onClick={(e) => {
                                if (!assessClientId) e.preventDefault();
                              }}
                            >
                              Full page
                            </Link>
                          </div>
                        </div>
                      </div>

                      {assessClientId ? (
                        timelineLoading ? (
                          <div className="text-center py-4">
                            <div
                              className="spinner-border spinner-border-sm text-danger"
                              role="status"
                            />
                            <div className="text-muted mt-2 small">
                              Loading timeline…
                            </div>
                          </div>
                        ) : tlItems.length ? (
                          <>
                            <div className="pt-tl-summary">
                              <span className="pt-chip">
                                {num(tlItems.length)} assessments
                              </span>
                              {tlStats.first_date ? (
                                <span className="pt-chip">
                                  {moment(tlStats.first_date).format("DD MMM YYYY")} –{" "}
                                  {moment(tlStats.last_date).format("DD MMM YYYY")}
                                </span>
                              ) : null}
                              {deltaLabel(tlStats.weight_change) ? (
                                <span
                                  className={`pt-chip${deltaClass(
                                    tlStats.weight_change,
                                    true
                                  )}`}
                                >
                                  Weight {deltaLabel(tlStats.weight_change)} kg overall
                                </span>
                              ) : null}
                            </div>
                            <ol className="pt-timeline">
                              {tlItems.map((item) => (
                                <li
                                  className="pt-tl-item"
                                  key={`${item.kind}-${item.id}`}
                                >
                                  <span
                                    className={`pt-tl-dot pt-tl-dot-${item.kind}`}
                                  />
                                  <div className="pt-tl-card">
                                    <div className="pt-tl-head">
                                      <div>
                                        <strong className="pt-tl-date">
                                          {item.date
                                            ? moment(item.date).format("DD MMM YYYY")
                                            : "No date"}
                                        </strong>
                                        <span className="pt-tl-type">{item.type}</span>
                                      </div>
                                      <Link
                                        className="btn btn-sm btn-outline-dark"
                                        to={`/viewAssessmentInfo/${assessClientId}`}
                                      >
                                        View
                                      </Link>
                                    </div>
                                    <p className="pt-tl-meta">
                                      Added by{" "}
                                      <strong>{item.added_by || "Unknown"}</strong>
                                      {item.recorded_at
                                        ? ` · recorded ${moment(item.recorded_at).format(
                                            "DD MMM YYYY, hh:mm A"
                                          )}`
                                        : ""}
                                    </p>
                                    {item.metrics ? (
                                      <div className="pt-tl-metrics">
                                        {METRIC_CHIPS.filter(
                                          (m) =>
                                            item.metrics[m.key] !== null &&
                                            item.metrics[m.key] !== undefined &&
                                            item.metrics[m.key] !== ""
                                        ).map((m) => {
                                          const change = deltaLabel(
                                            item.changes?.[m.key]
                                          );
                                          return (
                                            <span className="pt-tl-metric" key={m.key}>
                                              <span className="pt-tl-metric-label">
                                                {m.label}
                                              </span>
                                              <span className="pt-tl-metric-value">
                                                {item.metrics[m.key]}
                                                {m.unit ? ` ${m.unit}` : ""}
                                              </span>
                                              {change ? (
                                                <span
                                                  className={`pt-tl-metric-delta${deltaClass(
                                                    item.changes?.[m.key],
                                                    m.lowerIsBetter
                                                  )}`}
                                                >
                                                  {change}
                                                </span>
                                              ) : null}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    ) : item.fitness_goals ? (
                                      <p className="pt-tl-goals">
                                        Goal: {item.fitness_goals}
                                      </p>
                                    ) : null}
                                  </div>
                                </li>
                              ))}
                            </ol>
                          </>
                        ) : (
                          <p className="pt-empty">
                            No assessments recorded for this client yet. Use “Add
                            Assessment” to create the first one.
                          </p>
                        )
                      ) : assessments.length ? (
                        <div className="pt-table-wrap">
                          <table className="pt-table">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Client</th>
                                <th>Added by</th>
                                <th>Weight</th>
                                <th>BMI</th>
                                <th>Fat %</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {assessments.map((row) => (
                                <tr key={row.id}>
                                  <td>
                                    {row.date
                                      ? moment(row.date).format("DD MMM YYYY")
                                      : "—"}
                                    {row.recorded_at ? (
                                      <div className="small text-muted">
                                        recorded{" "}
                                        {moment(row.recorded_at).format(
                                          "DD MMM, hh:mm A"
                                        )}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    <Link to={`/client-home/${row.client_id}`}>
                                      {row.client_name || `#${row.client_id}`}
                                    </Link>
                                    <div className="small text-muted">
                                      {num(row.client_assessment_count)} total
                                    </div>
                                  </td>
                                  <td>{row.added_by || "—"}</td>
                                  <td>{row.weight ?? "—"}</td>
                                  <td>{row.body_mass_index ?? "—"}</td>
                                  <td>{row.fat ?? "—"}</td>
                                  <td>
                                    <div className="pt-row-actions">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() =>
                                          setAssessClientId(String(row.client_id))
                                        }
                                      >
                                        Timeline
                                      </button>
                                      <Link
                                        className="btn btn-sm btn-outline-dark"
                                        to={`/viewAssessmentInfo/${row.client_id}`}
                                      >
                                        View
                                      </Link>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="pt-empty">
                          No assessments in this date range. Pick a client above to
                          see their full history.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-xl-5">
                    <div className="pt-panel">
                      <div className="pt-panel-head">
                        <h5>Refer to BeFit</h5>
                        <span className="pt-chip">Push client to a PT</span>
                      </div>
                      <form onSubmit={submitReferral}>
                        <div className="mb-2">
                          <label className="pt-field-label" htmlFor="gt-client-search">
                            Search client
                          </label>
                          <ClientPicker
                            inputId="gt-client-search"
                            clients={clients}
                            value={referral.client_id}
                            placeholder="Type a name, ID, or phone from your branch"
                            onPick={(c) =>
                              setReferral((prev) => ({
                                ...prev,
                                client_id: c ? String(c.id) : "",
                                client_name: c?.name || "",
                              }))
                            }
                          />
                        </div>
                        <div className="mb-2">
                          <label className="pt-field-label" htmlFor="gt-trainer">
                            Personal Trainer
                          </label>
                          <select
                            id="gt-trainer"
                            className="form-select"
                            value={referral.trainer_id}
                            onChange={(e) =>
                              setReferral((prev) => ({
                                ...prev,
                                trainer_id: e.target.value,
                              }))
                            }
                            required
                          >
                            <option value="">Select PT…</option>
                            {trainers.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-2">
                          <label className="pt-field-label" htmlFor="gt-referral-date">
                            Referral date
                          </label>
                          <input
                            id="gt-referral-date"
                            type="date"
                            className="form-control"
                            value={referral.referral_date}
                            onChange={(e) =>
                              setReferral((prev) => ({
                                ...prev,
                                referral_date: e.target.value,
                              }))
                            }
                            required
                          />
                        </div>
                        <div className="mb-3">
                          <label className="pt-field-label" htmlFor="gt-notes">
                            Notes
                          </label>
                          <textarea
                            id="gt-notes"
                            className="form-control"
                            rows={2}
                            maxLength={500}
                            value={referral.notes}
                            onChange={(e) =>
                              setReferral((prev) => ({
                                ...prev,
                                notes: e.target.value,
                              }))
                            }
                          />
                        </div>
                        <button
                          type="submit"
                          className="btn btn-danger"
                          disabled={saving}
                        >
                          {saving ? "Saving…" : "Save BeFit referral"}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>

                <h3 className="pt-section-title">BeFit conversions</h3>
                <div className="pt-panel mb-4">
                  <div className="pt-panel-head">
                    <h5>{data?.viewer_is_gt === false ? "BeFit referrals" : "My BeFit referrals"}</h5>
                    <span className="pt-chip">
                      {num(stats.converted)} converted · {num(stats.pending)} pending
                    </span>
                  </div>
                  {referrals.length ? (
                    <div className="pt-table-wrap">
                      <table className="pt-table">
                        <thead>
                          <tr>
                            <th>Referral date</th>
                            <th>Client</th>
                            <th>Phone</th>
                            {data?.viewer_is_gt === false ? <th>General Trainer</th> : null}
                            <th>Personal Trainer</th>
                            <th>Notes</th>
                            <th>Status</th>
                            <th>Converted to PT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {referrals.map((row) => (
                            <tr key={row.id}>
                              <td>
                                {row.referral_date
                                  ? moment(row.referral_date).format("DD MMM YYYY")
                                  : "—"}
                              </td>
                              <td>
                                <Link to={`/client-home/${row.client_id}`}>
                                  {row.client_name}
                                </Link>
                              </td>
                              <td>{row.client_phone || "—"}</td>
                              {data?.viewer_is_gt === false ? <td>{row.gt_name}</td> : null}
                              <td>{row.trainer_name}</td>
                              <td>{row.notes || "—"}</td>
                              <td>
                                <span
                                  className={`pt-pill ${conversionPill(
                                    row.converted_to_pt
                                  )}`}
                                >
                                  {row.converted_to_pt || "Pending"}
                                </span>
                                {row.converted_at ? (
                                  <div className="small text-muted">
                                    {moment(row.converted_at).format("DD MMM YYYY")}
                                  </div>
                                ) : null}
                              </td>
                              <td>
                                <select
                                  className="form-select form-select-sm"
                                  value={row.converted_to_pt || "Pending"}
                                  onChange={(e) =>
                                    updateConversion(row.id, e.target.value)
                                  }
                                  aria-label="Conversion status"
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Yes">Yes</option>
                                  <option value="No">No</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="pt-empty">
                      No BeFit referrals yet. The client, phone, and personal trainer show here after you save one.
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
